import { useNavigate, useParams } from 'react-router-dom';
import { Asset } from '../../types';
import { Layout } from '../../components/layout';
import { useAuth } from 'react-oidc-context';
import { getEndpoint } from '../../utils';
import { useEffect, useState } from 'react';
import { Box, Button, Container, Header, SpaceBetween, BreadcrumbGroup } from '@cloudscape-design/components';
import { RecordPageView } from '../../utils/rum';

export default function AssetDetails() {
    RecordPageView();
    const navigate = useNavigate();
    const auth = useAuth();
    const { path } = useParams();
    const [asset, setAsset] = useState<Asset>();

    useEffect(() => {
        if (auth.isAuthenticated && !asset) {
            const token = auth.user?.id_token;
            if (!token) return;

            // Remove any leading slash before encoding
            const cleanPath = path!.replace(/^\/+/, '');
            const endpoint = getEndpoint(`/api/admin/assets/${encodeURIComponent(cleanPath)}`);
            fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            }).then((response) => {
                if (response.ok) {
                    response.json().then((data) => setAsset(data));
                }
            }).catch(console.error);
        }
    }, [auth, asset, path]);

    const handleDelete = async () => {
        try {
            const cleanPath = path!.replace(/^\/+/, '');
            const response = await fetch(getEndpoint(`/api/admin/assets/${encodeURIComponent(cleanPath)}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${auth.user?.id_token}`,
                },
            });
            if (response.ok) {
                navigate('/admin');
            }
        } catch (error) {
            console.error('Failed to delete asset:', error);
        }
    };

    if (!asset) {
        return <Layout>Loading...</Layout>;
    }

    const isImage = asset.fileType.startsWith('image/');

    return (
        <Layout breadcrumbs={
            <BreadcrumbGroup
                onClick={({ detail }) => navigate(detail.href)} 
                items={[
                    { text: "Admin", href: "/admin" },
                    { text: "Asset Details", href: `admin/asset/${path}` }
                ]}
            />
        }>
            <Container>
                <SpaceBetween size="l">
                    <Header
                        variant="h1"
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={() => navigate('/admin')}>Back</Button>
                                <Button variant='normal' onClick={handleDelete}>Delete</Button>
                            </SpaceBetween>
                        }
                    >
                        Asset Details
                    </Header>

                    <Box>
                        <SpaceBetween size="m">
                            <div><strong>Path:</strong> {asset.path}</div>
                            <div><strong>Note:</strong> {asset.note}</div>
                            <div><strong>Type:</strong> {asset.fileType}</div>
                            <div><strong>Uploaded:</strong> {new Date(asset.uploadedAt).toLocaleString()}</div>
                            <div><strong>Last Modified:</strong> {new Date(asset.lastModified).toLocaleString()}</div>
                            {asset.size && <div><strong>Size:</strong> {Math.round(asset.size / 1024)} KB</div>}
                        </SpaceBetween>
                    </Box>

                    <Box>
                        {isImage ? (
                            <img 
                                src={`/${asset.path.replace(/^\/+/, '')}`} 
                                alt={asset.note} 
                                style={{ maxWidth: '100%', maxHeight: '500px' }} 
                            />
                        ) : (
                            <Button href={`/${asset.path.replace(/^\/+/, '')}`} target="_blank" download>
                                Download File
                            </Button>
                        )}
                    </Box>
                </SpaceBetween>
            </Container>
        </Layout>
    );
}
